import { useEffect, useState, useRef } from "react";
import type { ParsedCSV, SourceType } from "../../../../types/extraction";
import {
  startExtraction,
  getExtractionStatus,
} from "../../../../services/extractions";
import { parseExtractionData } from "../../../../utils/extraction-response-parser";
import { parsePlayerData } from "../../../../utils/text-extraction-parser";
import { validateRoster } from "../../../../utils/roster-validator";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { SentryService } from "../../../../services/sentry";
import "./ExtractionProgress.css";

interface ExtractionProgressProps {
  gcsUrl: string;
  sourceType: SourceType;
  uploadType?: string;
  /**
   * Called with the parsed roster and the id of the extraction that produced it
   * (`roster_extractions.id`). The id rides on the team-creation commit as
   * `extractionId` so a system admin can trace each prospect back to this upload.
   */
  onComplete: (data: ParsedCSV, extractionId: string) => void;
  onError: (error: string) => void;
  onRetry: () => void;
}

const POLL_INTERVAL_MS = 2000;
const SLOW_EXTRACTION_THRESHOLD_MS = 60000;

export function ExtractionProgress({
  gcsUrl,
  sourceType,
  uploadType,
  onComplete,
  onError,
  onRetry,
}: ExtractionProgressProps) {
  const [phase, setPhase] = useState<"assessing" | "extracting">("assessing");
  const [error, setError] = useState<string>("");
  const [isTakingLong, setIsTakingLong] = useState(false);

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    const abortController = new AbortController();
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    const slowTimer = setTimeout(() => {
      setIsTakingLong(true);
    }, SLOW_EXTRACTION_THRESHOLD_MS);

    const extract = async () => {
      const startTime = Date.now();

      try {
        SentryService.setUploadContext({
          trace_id: "",
          source_type: sourceType,
          gcs_url: gcsUrl,
          operation: "start_extraction",
          phase: "starting",
        });

        setPhase("assessing");
        SentryService.addUploadBreadcrumb("Starting async extraction", {
          source_type: sourceType,
          gcs_url: gcsUrl,
        });

        // Start the async extraction — returns immediately
        const startResponse = await startExtraction(
          [gcsUrl],
          uploadType,
          abortController.signal
        );

        if (abortController.signal.aborted) return;

        const extractionId = startResponse.extractionId;

        SentryService.addUploadBreadcrumb("Extraction started", {
          extraction_id: extractionId,
        });

        // Poll for status
        await new Promise<void>((resolve, reject) => {
          pollInterval = setInterval(async () => {
            if (abortController.signal.aborted) {
              if (pollInterval) clearInterval(pollInterval);
              resolve();
              return;
            }

            try {
              const status = await getExtractionStatus(
                extractionId,
                abortController.signal
              );

              if (abortController.signal.aborted) return;

              // Update phase based on status
              if (
                status.status === "pending" ||
                status.status === "assessing"
              ) {
                setPhase("assessing");
              } else if (status.status === "extracting") {
                setPhase("extracting");
              } else if (status.status === "completed") {
                if (pollInterval) clearInterval(pollInterval);

                SentryService.addUploadBreadcrumb("Extraction completed", {
                  extraction_id: extractionId,
                  tokens_used: status.tokensUsed,
                  processing_time_ms: status.processingTimeMs,
                });

                let parsed: ParsedCSV;

                if (status.rosterType && !status.assessment) {
                  // CSV/Excel path — use text extraction parser
                  parsed = parsePlayerData({
                    extractionId: status.extractionId,
                    success: true,
                    players: status.players!,
                    columns: status.columns,
                    rosterType: status.rosterType as "single_team" | "showcase",
                  });
                } else {
                  // Image/PDF path — use 2-step parser
                  parsed = parseExtractionData(
                    {
                      extractionId: status.extractionId,
                      success: true,
                      assessment: status.assessment!,
                    },
                    {
                      extractionId: status.extractionId,
                      success: true,
                      players: status.players!,
                      columns: status.columns,
                    }
                  );
                }

                // Validate
                const validation = validateRoster(parsed);

                if (!validation.isValid) {
                  const validationError = new Error(
                    validation.errors.join("\n")
                  );
                  SentryService.captureError(
                    "[EXTRACTION FAILED] Roster validation failed",
                    {
                      operation: "extract",
                      phase: "validation",
                      level: "error",
                      tags: {
                        error_category: "extraction_failed",
                        upload_phase: "extraction",
                        screen: "ExtractionProgress",
                      },
                      extra: {
                        validation_errors: validation.errors,
                        source_type: sourceType,
                        gcs_url: gcsUrl,
                        extraction_id: extractionId,
                        duration_ms: Date.now() - startTime,
                      },
                      fingerprint: ["roster-upload", "validation", "failed"],
                    }
                  );
                  reject(validationError);
                  return;
                }

                if (validation.warnings.length > 0) {
                  console.warn("Extraction warnings:", validation.warnings);
                }

                if (!abortController.signal.aborted) {
                  onCompleteRef.current(parsed, extractionId);
                }
                resolve();
              } else if (status.status === "failed") {
                if (pollInterval) clearInterval(pollInterval);
                const failError = new Error(
                  status.error || "Extraction failed"
                );
                SentryService.captureError(
                  "[EXTRACTION FAILED] Async extraction failed",
                  {
                    operation: "extract",
                    phase: "failed",
                    tags: {
                      error_category: "extraction_failed",
                      upload_phase: "extraction",
                      screen: "ExtractionProgress",
                    },
                    extra: {
                      error: status.error,
                      source_type: sourceType,
                      gcs_url: gcsUrl,
                      extraction_id: extractionId,
                      duration_ms: Date.now() - startTime,
                    },
                    fingerprint: [
                      "roster-upload",
                      "async-extraction",
                      "failed",
                    ],
                  }
                );
                reject(failError);
              }
            } catch (pollErr) {
              // If polling itself fails (network error), don't stop — let the next poll retry
              if (abortController.signal.aborted) return;
              SentryService.captureError(
                "[EXTRACTION WARNING] Status poll request failed, retrying",
                {
                  operation: "extract_async",
                  phase: "polling",
                  level: "warning",
                  tags: {
                    error_category: "poll_error",
                    upload_phase: "extraction",
                    screen: "ExtractionProgress",
                  },
                  extra: {
                    error:
                      pollErr instanceof Error
                        ? pollErr.message
                        : String(pollErr),
                    extraction_id: extractionId,
                    source_type: sourceType,
                    duration_ms: Date.now() - startTime,
                  },
                  fingerprint: ["roster-upload", "poll-failure"],
                }
              );
              console.warn(
                "Extraction status poll failed, retrying...",
                pollErr
              );
            }
          }, POLL_INTERVAL_MS);
        });
      } catch (err) {
        if (abortController.signal.aborted) {
          // Component unmounted during extraction — log to Sentry but don't update state
          SentryService.captureError(
            "[EXTRACTION ABORTED] Extraction was interrupted before completion",
            {
              operation: "extract_async",
              phase: phase,
              level: "warning",
              tags: {
                error_category: "extraction_aborted",
                upload_phase: "extraction",
                screen: "ExtractionProgress",
              },
              extra: {
                source_type: sourceType,
                gcs_url: gcsUrl,
                phase: phase,
                duration_ms: Date.now() - startTime,
              },
              fingerprint: ["roster-upload", "extraction-aborted"],
            }
          );
          return;
        }

        const errorMessage = getSafeErrorMessage(err);

        SentryService.captureError(
          "[EXTRACTION FAILED] Extraction process failed",
          {
            operation: "extract_async",
            phase: phase,
            tags: {
              error_category: "extraction_failed",
              upload_phase: "extraction",
              screen: "ExtractionProgress",
            },
            extra: {
              error: err instanceof Error ? err.message : String(err),
              source_type: sourceType,
              gcs_url: gcsUrl,
              phase: phase,
              duration_ms: Date.now() - startTime,
              error_message: errorMessage,
            },
            fingerprint: ["roster-upload", "extraction-general", "failed"],
          }
        );

        setError(errorMessage);
        onErrorRef.current(errorMessage);
      }
    };

    extract();

    return () => {
      abortController.abort();
      if (pollInterval) clearInterval(pollInterval);
      clearTimeout(slowTimer);
    };
  }, [gcsUrl, sourceType]); // onComplete/onError handled via refs to prevent re-extraction

  if (error) {
    return (
      <div className="extraction-progress">
        <div className="extraction-error">
          <div className="error-icon">⚠️</div>
          <h3>Extraction Failed</h3>
          <p>{error}</p>
          <div className="button-group">
            <button className="retry-button" onClick={onRetry}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="extraction-progress">
      <div className="extraction-content">
        <div className="spinner" />
        <p className="phase-text">
          {phase === "assessing"
            ? "Analyzing structure..."
            : "Extracting player data..."}
        </p>
        <p className="subtext">This might take a minute</p>
        {isTakingLong && (
          <p className="slow-warning">
            This is taking a while, try cancelling and restarting this
            extraction if it feels like it's taking too long
          </p>
        )}
      </div>
    </div>
  );
}
